// [PURPOSE]: UI Context - Centralized UI state management
// [WHY]: Extract UI-related state from monolithic App.jsx (16,437 lines)
// [INTEGRATION]: Provides modals, dialogs, collapsed panels, and UI state via React Context
// [CREATED]: October 24, 2025 - Phase 4A: Architectural Refactoring

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { safeLocalStorageSet } from '../utils/storageValidator';

/**
 * UIContext provides all UI-related state and functions
 *
 * This context manages:
 * - Active tab navigation
 * - Status messages and tooltips
 * - Modal and dialog visibility
 * - Collapsed panel states
 * - Touch gesture state
 * - Quick menu position
 * - Activity log expansion
 * - Journal and roll table UI
 * - Spell dialogs and editors
 */
const UIContext = createContext(null);

export const UIProvider = ({ children }) => {
  // ============================================================================
  // NAVIGATION STATE
  // ============================================================================

  const [activeTab, setActiveTab] = useState('inventory');

  // ============================================================================
  // STATUS & MESSAGING STATE
  // ============================================================================

  const [statusMessage, setStatusMessage] = useState('');
  const [statusTooltip, setStatusTooltip] = useState(null);
  const [statusType, setStatusType] = useState('info');

  // [UI POLISH]: Auto-detect status message type from content when not explicitly passed
  const detectStatusType = useCallback((message) => {
    if (!message) return 'info';
    if (/^❌|^Error|error/i.test(message)) return 'error';
    if (/^✅|added|saved|Healed|removed|equipped|learned|prepared|cured|stabilized|gained|restored|recovered/i.test(message)) return 'success';
    if (/^⚠️|warning|caution/i.test(message)) return 'warning';
    return 'info';
  }, []);

  // [UI POLISH]: Extended showStatus with optional type parameter
  // Signature: showStatus(message, duration, details, type)
  // All 622 existing callers work unchanged — auto-detect handles them
  //
  // [BUG-SHOWSTATUS-STALE-TIMER FIX 2026-07-18]: each call arms a wipe timer, and the
  // previous call's timer was never cancelled — so any toast shown within `duration` of
  // an earlier one was wiped EARLY when the stale timer fired (e.g. take damage, then
  // short rest ~2.5s later: '😴 Short rest completed' survived ~500ms instead of 3s).
  // Found live by the sim harness: the rest action's completion witness read the toast
  // 800ms after the click and it was already gone. Each new message now owns the wipe.
  const statusTimerRef = useRef(null);
  const showStatus = useCallback((message, duration = 3000, details = null, type = null) => {
    setStatusMessage(message);
    setStatusType(type || detectStatusType(message));
    if (details) {
      setStatusTooltip(details);
    }
    if (statusTimerRef.current !== null) {
      clearTimeout(statusTimerRef.current);
    }
    statusTimerRef.current = setTimeout(() => {
      statusTimerRef.current = null;
      setStatusMessage('');
      setStatusTooltip(null);
      setStatusType('info');
    }, duration);
  }, [detectStatusType]);

  // ============================================================================
  // VIEW MODE STATE
  // ============================================================================

  const [showSpreadsheetView, setShowSpreadsheetView] = useState(false);

  // ============================================================================
  // COLLAPSED PANEL STATE
  // ============================================================================

  const [statsCollapsed, setStatsCollapsed] = useState(false);
  const [equipmentCollapsed, setEquipmentCollapsed] = useState(false);
  const [quickBonusesCollapsed, setQuickBonusesCollapsed] = useState(false);
  // [FIX MAY 2026]: Inventory rail Grid Config + Currency now collapsible to
  // match sibling panels. Default expanded (matches statsCollapsed/equipmentCollapsed).
  const [gridConfigCollapsed, setGridConfigCollapsed] = useState(false);
  const [currencyCollapsed, setCurrencyCollapsed] = useState(false);

  // ============================================================================
  // MODAL & DIALOG STATE
  // ============================================================================

  // Character dialogs
  const [showPresetManager, setShowPresetManager] = useState(false);
  const [hiddenPresets, setHiddenPresets] = useState([]); // [PHASE 4C]: Hidden quick bonus presets
  const [customBonus, setCustomBonus] = useState({}); // [PHASE 4C]: Form state for custom bonus creator

  // Shop dialogs
  const [showShopTables, setShowShopTables] = useState(false);
  const [generatedShop, setGeneratedShop] = useState(null);
  const [activeShops, setActiveShops] = useState([]);
  const [isAddingToShop, setIsAddingToShop] = useState(false);
  const [selectedShopIndex, setSelectedShopIndex] = useState(null);
  const [showShopPurchase, setShowShopPurchase] = useState(false);
  const [showCustomShopCreator, setShowCustomShopCreator] = useState(false);
  const [showCustomTableCreator, setShowCustomTableCreator] = useState(false);
  // [PHASE B / #057 — 2026-05-07]: Edit Shop dialog state. Smoke-test
  //   2026-05-07: "cant edit shop after its made; I cant change the
  //   type of tables or item rolls after". The dialog opens with the
  //   target shop loaded; user can rename, change shopkeeper, change
  //   markup, change size, and re-roll items in place. Save mutates
  //   the existing shop in activeShops + generatedShop (when it's the
  //   active one) instead of creating a new shop.
  const [showEditShopDialog, setShowEditShopDialog] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  // [PHASE B / #059 — 2026-05-07]: Edit Custom Shop Type. When set, the
  //   Custom Shop Type Creator dialog opens in edit mode — pre-fills the
  //   form from the existing entry, save replaces in place (id retained)
  //   instead of pushing new. Null = create-new mode (legacy behavior).
  const [editingCustomShopType, setEditingCustomShopType] = useState(null);
  const [recentShopRolls, setRecentShopRolls] = useState([]);
  const [shopSettings, setShopSettings] = useState({
    shopType: 'general',
    shopSize: 'medium',
    shopName: '',
    shopkeeper: '',
    markup: 150,
    generationMode: 'tables',  // Default to tables mode for premium
    selectedTables: [],  // Which roll tables to use for generation
    useSimpleMode: false  // Toggle between simple category mode and advanced table mode
  });
  const [customShopTypes, setCustomShopTypes] = useState([]);

  // ============================================================================
  // TOUCH GESTURE STATE (for mobile swipe navigation)
  // ============================================================================

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // ============================================================================
  // QUICK MENU POSITION
  // ============================================================================

  const [quickMenuPosition, setQuickMenuPosition] = useState(() => {
    try {
      const saved = localStorage.getItem('quickMenuPosition');
      return saved ? JSON.parse(saved) : null; // null = use default (right center)
    } catch {
      return null;
    }
  });

  // Save quick menu position to localStorage
  useEffect(() => {
    if (quickMenuPosition) {
      try {
        safeLocalStorageSet('quickMenuPosition', JSON.stringify(quickMenuPosition));
      } catch (error) {
        console.error('Failed to save quick menu position:', error);
      }
    }
  }, [quickMenuPosition]);

  // ============================================================================
  // ACTIVITY LOG UI STATE
  // ============================================================================

  const [expandedLogEntries, setExpandedLogEntries] = useState(new Set());

  // [PHASE 4C - NOV 1, 2025]: Activity log filtering and search
  const [logFilters, setLogFilters] = useState({
    combat: true,
    items: true,
    currency: true,
    spells: true,
    character: true,
    status: true,
    system: true
  });
  const [logSearchTerm, setLogSearchTerm] = useState('');

  const toggleLogEntry = useCallback((entryId) => {
    setExpandedLogEntries(prev => {
      const updated = new Set(prev);
      if (updated.has(entryId)) {
        updated.delete(entryId);
      } else {
        updated.add(entryId);
      }
      return updated;
    });
  }, []);

  const expandLogEntry = useCallback((entryId) => {
    setExpandedLogEntries(prev => {
      const updated = new Set(prev);
      updated.add(entryId);
      return updated;
    });
  }, []);

  const collapseLogEntry = useCallback((entryId) => {
    setExpandedLogEntries(prev => {
      const updated = new Set(prev);
      updated.delete(entryId);
      return updated;
    });
  }, []);

  const collapseAllLogEntries = useCallback(() => {
    setExpandedLogEntries(new Set());
  }, []);

  // ============================================================================
  // JOURNAL UI STATE
  // ============================================================================

  const [showJournalEditor, setShowJournalEditor] = useState(false);
  const [editingJournalEntry, setEditingJournalEntry] = useState(null);
  const [journalFilter, setJournalFilter] = useState({ category: 'all', tags: '', sortBy: 'newest' });

  // ============================================================================
  // ROLL TABLE UI STATE
  // ============================================================================

  const [journalRollTables, setJournalRollTables] = useState([]); // [PHASE 4C]: Roll tables for journal integration
  const [selectedRollTable, setSelectedRollTable] = useState(null);
  const [showTableCreator, setShowTableCreator] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [selectedTableCategory, setSelectedTableCategory] = useState('all');

  // Table creator state
  const [tableType, setTableType] = useState('simple');
  const [weightedEntries, setWeightedEntries] = useState([{ min: '', max: '', text: '' }]);

  // ============================================================================
  // SPELL DIALOG STATE
  // ============================================================================

  const [showQuickcastDialog, setShowQuickcastDialog] = useState(false);
  const [quickcastSpell, setQuickcastSpell] = useState(null);
  const [lastSpellRoll, setLastSpellRoll] = useState(null);
  const [showSpellSelection, setShowSpellSelection] = useState(false);
  const [showCustomSpellCreator, setShowCustomSpellCreator] = useState(false);
  const [templateSpell, setTemplateSpell] = useState(null);
  const [editingSpell, setEditingSpell] = useState(null);

  // Wizard spellbook & scribing dialogs
  const [showSpellCopyDialog, setShowSpellCopyDialog] = useState(false);
  const [spellCopyData, setSpellCopyData] = useState(null);
  const [showScrollScribeDialog, setShowScrollScribeDialog] = useState(false);

  // ============================================================================
  // AUTOSAVE STATE
  // ============================================================================

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastAutosave, setLastAutosave] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // [PURPOSE]: Format relative time for autosave indicator
  const getRelativeTime = (date) => {
    if (!date) return null;
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  // [PURPOSE]: Force re-render every 30s to update relative time display
  const [relativeTimeUpdate, setRelativeTimeUpdate] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setRelativeTimeUpdate(prev => prev + 1);
    }, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // ============================================================================
  // UI ACTIONS
  // ============================================================================

  const navigateToTab = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const togglePanel = useCallback((panelName) => {
    switch (panelName) {
      case 'stats':
        setStatsCollapsed(prev => !prev);
        break;
      case 'equipment':
        setEquipmentCollapsed(prev => !prev);
        break;
      case 'quickBonuses':
        setQuickBonusesCollapsed(prev => !prev);
        break;
      default:
        console.warn(`Unknown panel: ${panelName}`);
    }
  }, []);

  // [ORPHAN SWEEP — 2026-07-09, APPJSX-MULTICLASS-DEAD ruling]: this command layer had
  // 13 openDialog cases, a mirror closeDialog switch, and a closeAllDialogs utility —
  // with exactly ONE live caller app-wide (SpellbookTab's openDialog('scrollScribe')).
  // That indirection is how the Add-Multiclass dialog went dead unnoticed: its only
  // opener was an uncalled registry case. Both switches are trimmed to the live case
  // (every dialog stays reachable through its direct setShowX setter, which IS exported
  // and used); closeAllDialogs (zero callers) is deleted. Re-add a case only WITH its
  // caller in the same commit (Rule 25's spirit). Removed cases recoverable from git.
  const openDialog = useCallback((dialogName) => {
    switch (dialogName) {
      case 'scrollScribe':
        setShowScrollScribeDialog(true);
        break;
      default:
        console.warn(`Unknown dialog: ${dialogName}`);
    }
  }, []);

  const closeDialog = useCallback((dialogName) => {
    switch (dialogName) {
      case 'scrollScribe':
        setShowScrollScribeDialog(false);
        break;
      default:
        console.warn(`Unknown dialog: ${dialogName}`);
    }
  }, []);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  // [FIX APR 2026 — AUDIT C-4]: Memoize context value to prevent unnecessary consumer re-renders
  const value = useMemo(() => ({
    // Navigation
    activeTab,
    setActiveTab,
    navigateToTab,

    // Status & Messaging
    statusMessage,
    setStatusMessage,
    statusTooltip,
    setStatusTooltip,
    statusType,
    showStatus,

    // View Mode
    showSpreadsheetView,
    setShowSpreadsheetView,

    // Collapsed Panels
    statsCollapsed,
    setStatsCollapsed,
    equipmentCollapsed,
    setEquipmentCollapsed,
    quickBonusesCollapsed,
    setQuickBonusesCollapsed,
    gridConfigCollapsed,
    setGridConfigCollapsed,
    currencyCollapsed,
    setCurrencyCollapsed,
    togglePanel,

    // Character Dialogs
    showPresetManager,
    setShowPresetManager,
    hiddenPresets, // [PHASE 4C]
    setHiddenPresets, // [PHASE 4C]
    customBonus, // [PHASE 4C]
    setCustomBonus, // [PHASE 4C]

    // Shop Dialogs & State
    showShopTables,
    setShowShopTables,
    generatedShop,
    setGeneratedShop,
    activeShops,
    setActiveShops,
    isAddingToShop,
    setIsAddingToShop,
    selectedShopIndex,
    setSelectedShopIndex,
    showShopPurchase,
    setShowShopPurchase,
    showCustomShopCreator,
    setShowCustomShopCreator,
    showCustomTableCreator,
    setShowCustomTableCreator,
    showEditShopDialog,
    setShowEditShopDialog,
    editingShop,
    setEditingShop,
    editingCustomShopType,
    setEditingCustomShopType,
    recentShopRolls,
    setRecentShopRolls,
    shopSettings, // [PHASE 4C - SESSION 7]
    setShopSettings, // [PHASE 4C - SESSION 7]
    customShopTypes, // [PHASE 4C - SESSION 7]
    setCustomShopTypes, // [PHASE 4C - SESSION 7]

    // Touch Gestures
    touchStart,
    setTouchStart,
    touchEnd,
    setTouchEnd,

    // Quick Menu
    quickMenuPosition,
    setQuickMenuPosition,

    // Activity Log
    expandedLogEntries,
    setExpandedLogEntries,
    toggleLogEntry,
    expandLogEntry,
    collapseLogEntry,
    collapseAllLogEntries,
    logFilters, // [PHASE 4C]
    setLogFilters, // [PHASE 4C]
    logSearchTerm, // [PHASE 4C]
    setLogSearchTerm, // [PHASE 4C]

    // Journal
    showJournalEditor,
    setShowJournalEditor,
    editingJournalEntry,
    setEditingJournalEntry,
    journalFilter,
    setJournalFilter,

    // Roll Tables
    journalRollTables, // [PHASE 4C]
    setJournalRollTables, // [PHASE 4C]
    selectedRollTable,
    setSelectedRollTable,
    showTableCreator,
    setShowTableCreator,
    editingTable,
    setEditingTable,
    selectedTableCategory,
    setSelectedTableCategory,
    tableType,
    setTableType,
    weightedEntries,
    setWeightedEntries,

    // Spell Dialogs
    showQuickcastDialog,
    setShowQuickcastDialog,
    quickcastSpell,
    setQuickcastSpell,
    lastSpellRoll,
    setLastSpellRoll,
    showSpellSelection,
    setShowSpellSelection,
    showCustomSpellCreator,
    setShowCustomSpellCreator,
    templateSpell,
    setTemplateSpell,
    editingSpell,
    setEditingSpell,
    showSpellCopyDialog,
    setShowSpellCopyDialog,
    spellCopyData,
    setSpellCopyData,
    showScrollScribeDialog,
    setShowScrollScribeDialog,

    // Autosave
    hasUnsavedChanges,
    setHasUnsavedChanges,
    lastAutosave,
    setLastAutosave,
    isSaving,
    setIsSaving,
    getRelativeTime,
    relativeTimeUpdate,

    // Dialog Management
    openDialog,
    closeDialog
  }), [
    activeTab,
    setActiveTab,
    navigateToTab,
    statusMessage,
    setStatusMessage,
    statusTooltip,
    setStatusTooltip,
    statusType,
    showStatus,
    showSpreadsheetView,
    setShowSpreadsheetView,
    statsCollapsed,
    setStatsCollapsed,
    equipmentCollapsed,
    setEquipmentCollapsed,
    quickBonusesCollapsed,
    setQuickBonusesCollapsed,
    gridConfigCollapsed,
    setGridConfigCollapsed,
    currencyCollapsed,
    setCurrencyCollapsed,
    togglePanel,
    showPresetManager,
    setShowPresetManager,
    hiddenPresets,
    setHiddenPresets,
    customBonus,
    setCustomBonus,
    showShopTables,
    setShowShopTables,
    generatedShop,
    setGeneratedShop,
    activeShops,
    setActiveShops,
    isAddingToShop,
    setIsAddingToShop,
    selectedShopIndex,
    setSelectedShopIndex,
    showShopPurchase,
    setShowShopPurchase,
    showCustomShopCreator,
    setShowCustomShopCreator,
    showCustomTableCreator,
    setShowCustomTableCreator,
    showEditShopDialog,
    setShowEditShopDialog,
    editingShop,
    setEditingShop,
    editingCustomShopType,
    setEditingCustomShopType,
    recentShopRolls,
    setRecentShopRolls,
    shopSettings,
    setShopSettings,
    customShopTypes,
    setCustomShopTypes,
    touchStart,
    setTouchStart,
    touchEnd,
    setTouchEnd,
    quickMenuPosition,
    setQuickMenuPosition,
    expandedLogEntries,
    setExpandedLogEntries,
    toggleLogEntry,
    expandLogEntry,
    collapseLogEntry,
    collapseAllLogEntries,
    logFilters,
    setLogFilters,
    logSearchTerm,
    setLogSearchTerm,
    showJournalEditor,
    setShowJournalEditor,
    editingJournalEntry,
    setEditingJournalEntry,
    journalFilter,
    setJournalFilter,
    journalRollTables,
    setJournalRollTables,
    selectedRollTable,
    setSelectedRollTable,
    showTableCreator,
    setShowTableCreator,
    editingTable,
    setEditingTable,
    selectedTableCategory,
    setSelectedTableCategory,
    tableType,
    setTableType,
    weightedEntries,
    setWeightedEntries,
    showQuickcastDialog,
    setShowQuickcastDialog,
    quickcastSpell,
    setQuickcastSpell,
    lastSpellRoll,
    setLastSpellRoll,
    showSpellSelection,
    setShowSpellSelection,
    showCustomSpellCreator,
    setShowCustomSpellCreator,
    templateSpell,
    setTemplateSpell,
    editingSpell,
    setEditingSpell,
    showSpellCopyDialog,
    setShowSpellCopyDialog,
    spellCopyData,
    setSpellCopyData,
    showScrollScribeDialog,
    setShowScrollScribeDialog,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    lastAutosave,
    setLastAutosave,
    isSaving,
    setIsSaving,
    getRelativeTime,
    relativeTimeUpdate,
    openDialog,
    closeDialog
  ]);

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

/**
 * Hook to access UIContext
 * @throws {Error} if used outside UIProvider
 *
 * [FIX - NOV 3, 2025]: Enhanced error message with debugging hints
 */
export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    console.error('❌ useUI() called outside UIProvider!');
    console.error('📍 Check that your component is wrapped in <AppProviders>');
    console.error('📍 File: src/contexts/UIContext.js');
    throw new Error(
      'useUI must be used within UIProvider. ' +
      'Ensure your component is wrapped in <AppProviders>. ' +
      'See src/contexts/UIContext.js:557'
    );
  }
  return context;
};

export default UIContext;
